import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMessage } from "../../lib/api";
import { t, formatDate } from "../../lib/i18n";
import { Loader, EmptyState } from "../../components/Blocks";

const STATUSES = ["new", "reviewing", "contacted", "approved", "declined", "waitlisted"];

export default function AdminApplications() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/applications", { params: { status: status || undefined, q: q || undefined } });
    setItems(data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(errMessage(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const update = async (id, newStatus) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status: newStatus, review_notes: notes || null });
      toast.success(`Statut mis à jour : ${t(`statuses.${newStatus}`, newStatus)}`);
      setSelected(null);
      setNotes("");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (items === null) return <Loader />;

  return (
    <div data-testid="admin-applications">
      <h1 className="vb-display text-3xl lg:text-4xl font-bold">Candidatures</h1>

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <button className="vb-chip" data-active={status === ""} data-testid="admin-app-filter-all" onClick={() => setStatus("")}>
          Toutes
        </button>
        {STATUSES.map((s) => (
          <button key={s} className="vb-chip" data-active={status === s} data-testid={`admin-app-filter-${s}`} onClick={() => setStatus(s)}>
            {t(`statuses.${s}`, s)}
          </button>
        ))}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
          className="ml-auto flex items-center gap-3"
        >
          <input className="vb-input !py-2 w-48" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-app-search" />
          <button type="submit" className="vb-btn vb-btn-ghost !py-2" data-testid="admin-app-search-btn">
            Chercher
          </button>
        </form>
      </div>

      {items.length === 0 ? (
        <EmptyState message="Aucune candidature." testid="admin-applications-empty" />
      ) : (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="admin-applications-table">
            <thead>
              <tr className="text-left vb-label border-b border-white/10">
                <th className="py-3 pr-4">Nom</th>
                <th className="py-3 pr-4">Ville</th>
                <th className="py-3 pr-4">Intérêts</th>
                <th className="py-3 pr-4">18+</th>
                <th className="py-3 pr-4">Statut</th>
                <th className="py-3 pr-4">Reçue</th>
                <th className="py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-white/5" data-testid={`admin-application-row-${a.id}`}>
                  <td className="py-3 pr-4">
                    {a.first_name} {a.last_name}
                    <div className="text-xs text-neutral-600">{a.email}</div>
                  </td>
                  <td className="py-3 pr-4 text-neutral-400">{a.city}</td>
                  <td className="py-3 pr-4 text-neutral-400 text-xs">
                    {(a.interests || []).map((i) => t(`interests.${i}`, i)).join(", ")}
                  </td>
                  <td className="py-3 pr-4">
                    {a.confirms_age_18_plus ? (
                      <span className="text-[#34C759] text-xs">Confirmé</span>
                    ) : (
                      <span className="text-[#FF3B30] text-xs">Non</span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-xs px-2 py-1 border border-white/10">{t(`statuses.${a.status}`, a.status)}</span>
                  </td>
                  <td className="py-3 pr-4 text-xs text-neutral-600">{formatDate(a.created_at)}</td>
                  <td className="py-3">
                    <button
                      className="vb-btn vb-btn-ghost !py-1.5 !px-4 !text-xs"
                      data-testid={`admin-application-review-${a.id}`}
                      onClick={() => {
                        setSelected(a);
                        setNotes(a.review_notes || "");
                      }}
                    >
                      Réviser
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-start lg:items-center justify-center p-4 overflow-y-auto" data-testid="admin-application-modal">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl p-8 my-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="vb-display text-2xl">
                  {selected.first_name} {selected.last_name}
                </h2>
                <div className="text-xs text-neutral-500 mt-1">
                  {selected.email} · {selected.phone} · {selected.city}
                </div>
              </div>
              <button onClick={() => setSelected(null)} data-testid="admin-application-modal-close" className="text-neutral-500 hover:text-white">
                ✕
              </button>
            </div>
            <div className="mt-6 space-y-4 text-sm text-neutral-300">
              <div>
                <div className="vb-label">Intérêts</div>
                {(selected.interests || []).map((i) => t(`interests.${i}`, i)).join(", ")}
              </div>
              <div>
                <div className="vb-label">Présentation</div>
                {selected.short_introduction}
              </div>
              {selected.relevant_experience && (
                <div>
                  <div className="vb-label">Expérience</div>
                  {selected.relevant_experience}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-xs text-neutral-500">
                <div>Instagram : {selected.instagram || "—"}</div>
                <div>TikTok : {selected.tiktok || "—"}</div>
                <div>Audience : {selected.approximate_audience_size ?? "non fournie"}</div>
                <div>Langues : {(selected.languages || []).join(", ") || "—"}</div>
                <div>Disponibilité : {selected.availability || "—"}</div>
                <div>Consentement contact : {selected.consent_to_be_contacted ? "oui" : "non"}</div>
                <div>18+ : {selected.confirms_age_18_plus ? "confirmé" : "non"}</div>
                <div>Version consentement : {selected.consent_version}</div>
              </div>
              <div>
                <div className="vb-label">Notes internes</div>
                <textarea className="vb-input min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-application-notes" />
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  className={`vb-btn !py-2 !px-4 !text-xs ${s === "approved" ? "vb-btn-primary" : "vb-btn-ghost"}`}
                  data-testid={`admin-application-set-${s}`}
                  onClick={() => update(selected.id, s)}
                >
                  {t(`statuses.${s}`, s)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
